import { holdConfirmTokenFromAddressBar, forgetConfirmToken } from './helpers/confirmEmailLink';
import { initFaro } from './faro';
// Before Faro and before any request: a confirm link's token leaves the address bar first.
holdConfirmTokenFromAddressBar();
initFaro();

import axios from 'axios';
import { createPinia } from "pinia";
import io from "socket.io-client";
import { createApp, nextTick, reactive } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { arriveWithRoute, arrivesOn, endNavigation, startNavigation } from "./helpers/pageLoad";
import { isChunkLoadError, reloadForStaleBuild } from "./helpers/staleBuild";
import App from "./App.vue";
import "./chat.scss";
import { createHubGuard } from './routes/guard';
import { getSubdomain, getToken } from "./helpers/helper";
import { signOutOnLostSession } from "./helpers/lostSession";
import routes from "./routes/routes";
import { useAuthStore } from "./stores/auth";
import { useEventStore } from "./stores/event";
import "./style.scss";
import "./styles/general-sans.css";
import { useSocketStore } from "./stores/socketStore";
let userId;

// The page keeps room for its scrollbar whether or not it has one yet
// (style.scss), so it never shifts sideways as content arrives. On this app
// only: the session recorder shares the stylesheet and must not get a gutter.
document.documentElement.classList.add('xl-app');

const pinia = createPinia();
const authStore = useAuthStore(pinia);
const eventStore = useEventStore(pinia);
const _token = getToken();

await eventStore.getEvent();
if (_token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${_token}`;
    try {
        await authStore.retrieveUserData();
    } catch (error) {
        // 403 means user needs to register - will be redirected after router mounts
    }
    if(authStore.userData) {
        userId = authStore.userData.id;
    }
}
signOutOnLostSession(axios, authStore, () => window.location.replace('/login'));

// A hub no longer starts or ends (XN-4506), so these are empty on every hub the
// migration has been through. They stay because the Analytics pages still read
// them — each treating an empty value as "no date set" — and a
// hub that has not migrated yet keeps working unchanged.
window.eventOpenDate = eventStore.event.value.login_event_open_date || null;
window.eventClosedDate = eventStore.event.value.login_event_close_date || null;

const router = createRouter({
    history: createWebHistory(),
    routes,
});

window.store = reactive({
    historyChanges: [],
    // Set by a page that can save itself, so the "you have unsaved changes"
    // dialog in App.vue can offer Save alongside Discard. Null everywhere else.
    saveCurrentPage: null,
    changeRouteDialog: {
        show: false,
        toRoute: null,
        external: false,
    },
    eco_backend_url: import.meta.env.VITE_ECO_BACKEND_URL,
    event_backend_api: "/api",
    currentRoute: null,
    subdomain: getSubdomain(),
    subdomainName: eventStore.event.value.login_event_name,
    timezone: eventStore.event.value.timezone,
    userId,
    ecoEventStartsAt: eventStore.event.value.login_event_open_date,
    ecoEventEndsAt: eventStore.event.value.login_event_close_date,
    // socketHost: import.meta.env.VITE_SOCKET_HOST,
    // socket: io(import.meta.env.VITE_SOCKET_HOST, {
    //     transports: ["websocket"],
    // }),
});

const socketStore = useSocketStore(pinia);

// Loading (helpers/pageLoad): a route on its way keeps the progress line
// running, and its arrival fades the page in. Registered before the guard, so
// a slow guard counts too; a navigation that was stopped arrives nowhere.
router.beforeEach(() => { startNavigation(); });
router.beforeEach(createHubGuard(authStore, window.store, router));
router.afterEach(forgetConfirmToken);
router.afterEach((to, from, failure) => {
  endNavigation();
  if (!failure && arrivesOn(to, from)) nextTick(arriveWithRoute);
});
router.onError((error) => {
  endNavigation();
  // A page's chunk from a build that has since been replaced: load the current one.
  if (isChunkLoadError(error)) reloadForStaleBuild();
});

// Any other chunk a stale build asks for (Vite reports those itself).
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForStaleBuild()) event.preventDefault();
});

let previousRoute = null;

router.afterEach((to, from) => {
  // Save the previous route for the NEXT navigation
  window.store.lastRoute = previousRoute;

  // Update previousRoute to the route we just arrived at
  previousRoute = from.fullPath;
});

const app = createApp(App);
app.use(router);
app.use(pinia);

document
    .getElementById("pge-app")
    .style.setProperty("--font", "'Lato', sans-serif");
app.config.errorHandler = (err, vm, info) => {
    console.error("Vue Error:", err);
};

app.mount("#pge-app");
